import { msg } from '@gonfalon/messages/msg';

import type { UsageSeriesList } from '../../types';
import { AggregationType, DimensionType, Granularity, TimeRangeOption } from '../../types';
import { getGroupedTitle } from '../components/UsageDetail/components/UsageDetailGrouped';
import { dataExportCombinedDimensions } from '../constants/metricDimensions';

import {
  alignDataExportSeries,
  type DataExportCapabilitiesState,
  DataExportScope,
  type DataExportSource,
  type DataExportSourceStates,
  getScopeSources,
  hasDataExportSource,
} from './combineDataExportUsage';
import { getCutoffTimestampUTC, getPreviousTimestampsUTC } from './dates';
import { getSeriesTotal } from './getSeriesTotal';

const UNATTRIBUTED_DESTINATION_ID = '';
const UNATTRIBUTED_KEY = 'unattributed';
// The API reports every destination the caller cannot view in one series with this ID.
const HIDDEN_DESTINATION_ID = 'hidden';
export const BREAKDOWN_TOP_N = 5;
export function getBreakdownGroupBy(dimension: DimensionType, source: DataExportSource): DimensionType[] | undefined {
  if (dimension === DimensionType.EVENT_KIND) {
    return source === DataExportScope.STREAMING ? [dimension] : undefined;
  }
  if (dimension === DimensionType.ENVIRONMENT_ID && source === DataExportScope.WAREHOUSE) {
    return [dimension, DimensionType.PROJECT_ID];
  }
  return [dimension];
}

export function getBreakdownSources(dimension: DimensionType, scope: DataExportScope): DataExportSource[] {
  return getScopeSources(scope).filter((source) => getBreakdownGroupBy(dimension, source));
}

export function getBreakdownScope(
  scope: DataExportScope,
  hasBothSources: boolean,
  capabilities: DataExportCapabilitiesState | undefined,
  ...sourceSets: DataExportSourceStates[]
): DataExportScope {
  if (hasBothSources) {
    return scope;
  }
  if (hasDataExportSource(DataExportScope.STREAMING, capabilities, ...sourceSets)) {
    return DataExportScope.STREAMING;
  }
  return hasDataExportSource(DataExportScope.WAREHOUSE, capabilities, ...sourceSets)
    ? DataExportScope.WAREHOUSE
    : scope;
}

export function getBreakdownDimensions(scope: DataExportScope): DimensionType[] {
  return dataExportCombinedDimensions.filter((dimension) => getBreakdownSources(dimension, scope).length > 0);
}

export function getEffectiveBreakdownDimension(
  dimension: DimensionType | undefined,
  scope: DataExportScope,
): DimensionType | undefined {
  return dimension && getBreakdownDimensions(scope).includes(dimension) ? dimension : undefined;
}

export type BreakdownGroup = { key: string; label: string; total: number };

export type DestinationAvailability =
  | { status: 'available' }
  | { status: 'availableFrom'; day: number }
  | { status: 'notYetAvailable' };

export type DataExportBreakdownView =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'notYetAvailable' }
  | { status: 'availableFrom'; day: number; total: number }
  | {
      status: 'ready';
      total: number;
      ranked: BreakdownGroup[];
      top: BreakdownGroup[];
    };

function getUTCDayStart(time: number) {
  const date = new Date(time);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function getSourceDestinationAvailability(data: UsageSeriesList): DestinationAvailability | undefined {
  const isUnattributed = (index: string) =>
    data.metadata[Number(index)]?.[DimensionType.DESTINATION_ID] === UNATTRIBUTED_DESTINATION_ID;
  const usage = data.series.flatMap(({ time, ...values }) =>
    Object.entries(values)
      .filter(([, value]) => (value ?? 0) > 0)
      .map(([index]) => ({ time, isUnattributed: isUnattributed(index) })),
  );
  if (usage.length === 0) {
    return undefined;
  }
  const attributedTimes = usage.filter((point) => !point.isUnattributed).map(({ time }) => time);
  if (attributedTimes.length === 0) {
    return { status: 'notYetAvailable' };
  }
  const firstAttributedDay = getUTCDayStart(Math.min(...attributedTimes));
  const hasPreCutoverUsage = usage.some((point) => point.isUnattributed && point.time < firstAttributedDay);
  return hasPreCutoverUsage ? { status: 'availableFrom', day: firstAttributedDay } : { status: 'available' };
}

export function getDestinationAvailability(sources: Array<UsageSeriesList | undefined>): DestinationAvailability {
  const states = sources.flatMap((data) => (data ? [getSourceDestinationAvailability(data)] : []));
  if (states.some((state) => state?.status === 'notYetAvailable')) {
    return { status: 'notYetAvailable' };
  }
  const days = states.flatMap((state) => (state?.status === 'availableFrom' ? [state.day] : []));
  return days.length > 0 ? { status: 'availableFrom', day: Math.max(...days) } : { status: 'available' };
}

export type PendingDestinationSource = { source: DataExportSource; day?: number };

export function getPendingDestinationSource(
  dimension: DimensionType,
  scope: DataExportScope,
  sources: DataExportSourceStates,
): PendingDestinationSource | undefined {
  if (dimension !== DimensionType.DESTINATION_ID || scope !== DataExportScope.BOTH) {
    return undefined;
  }
  const states = getBreakdownSources(dimension, scope).map((source) => {
    const { data, isLoading, isError } = sources[source];
    return { source, availability: !isLoading && !isError && data ? getSourceDestinationAvailability(data) : null };
  });
  const available = states.filter(({ availability }) => availability?.status === 'available');
  const pending = states.flatMap(({ source, availability }) =>
    availability?.status === 'availableFrom' || availability?.status === 'notYetAvailable'
      ? [{ source, ...(availability.status === 'availableFrom' ? { day: availability.day } : {}) }]
      : [],
  );
  if (available.length !== 1 || pending.length !== 1) {
    return undefined;
  }
  return pending[0];
}

type InScopeData =
  | { status: 'loading' | 'error' | 'notYetAvailable' }
  | { status: 'ready'; data: Array<{ source: DataExportSource; data: UsageSeriesList }> };

function getInScopeData(
  dimension: DimensionType,
  scope: DataExportScope,
  sources: DataExportSourceStates,
): InScopeData {
  const inScope = getBreakdownSources(dimension, scope).sort(
    (left, right) => Number(right === DataExportScope.WAREHOUSE) - Number(left === DataExportScope.WAREHOUSE),
  );
  if (dimension === DimensionType.DESTINATION_ID && inScope.some((source) => sources[source].isUnsupportedGroupBy)) {
    return { status: 'notYetAvailable' };
  }
  if (inScope.some((source) => sources[source].isError)) {
    return { status: 'error' };
  }
  if (inScope.some((source) => sources[source].isLoading || !sources[source].data)) {
    return { status: 'loading' };
  }
  return {
    status: 'ready',
    data: inScope.map((source) => ({ source, data: sources[source].data as UsageSeriesList })),
  };
}

function getKey(dimension: DimensionType, metadata: UsageSeriesList['metadata'][number] | undefined) {
  const value = String(metadata?.[dimension] ?? '');
  return dimension === DimensionType.DESTINATION_ID && value === UNATTRIBUTED_DESTINATION_ID ? UNATTRIBUTED_KEY : value;
}

function getGroupLabel(dimension: DimensionType, metadata: UsageSeriesList['metadata'][number]) {
  const { environmentName, projectName } = metadata;
  if (dimension === DimensionType.DESTINATION_ID && getKey(dimension, metadata) === UNATTRIBUTED_KEY) {
    return msg('billing.dataExportCombined.unattributed');
  }
  if (dimension === DimensionType.ENVIRONMENT_ID && typeof environmentName === 'string') {
    const name = environmentName || getKey(dimension, metadata);
    return typeof projectName === 'string' ? `${projectName} / ${name}` : name;
  }
  return getGroupedTitle({ dimension, metadataKey: dimension, metadata }) || getKey(dimension, metadata);
}

function sumGroups(dimension: DimensionType, responses: UsageSeriesList[]) {
  const groups = new Map<string, BreakdownGroup>();
  const labelledWithProject = new Set<string>();
  const kinds = new Map<string, string>();
  let total = 0;
  for (const data of responses) {
    data.metadata.forEach((metadata, index) => {
      const groupTotal = getSeriesTotal({
        series: data.series,
        aggregationType: AggregationType.INCREMENTAL,
        granularity: Granularity.DAILY,
        metadataIdx: String(index),
      });
      const key = getKey(dimension, metadata);
      total += groupTotal;
      const group = groups.get(key) ?? { key, total: 0, label: '' };
      group.total += groupTotal;
      const hasProjectName = typeof metadata.projectName === 'string';
      if (!group.label || (hasProjectName && !labelledWithProject.has(key))) {
        group.label = getGroupLabel(dimension, metadata);
      }
      if (hasProjectName) {
        labelledWithProject.add(key);
      }
      if (typeof metadata.destinationKind === 'string' && !kinds.has(key)) {
        kinds.set(
          key,
          getGroupedTitle({ dimension: DimensionType.DESTINATION_KIND, metadataKey: 'destinationKind', metadata }),
        );
      }
      groups.set(key, group);
    });
  }
  return { groups, kinds, total };
}

function disambiguateLabels(groups: BreakdownGroup[], kinds: Map<string, string>): BreakdownGroup[] {
  const counts = new Map<string, number>();
  groups.forEach(({ label }) => counts.set(label, (counts.get(label) ?? 0) + 1));
  return groups.map((group) =>
    (counts.get(group.label) ?? 0) > 1
      ? {
          ...group,
          label: [
            group.label,
            kinds.get(group.key),
            // The hidden bucket's ID is a sentinel, not a destination; its tail would read "…dden".
            group.key === HIDDEN_DESTINATION_ID || group.key === UNATTRIBUTED_KEY
              ? undefined
              : `\u2026${group.key.slice(-4)}`,
          ]
            .filter(Boolean)
            .join(' \u00b7 '),
        }
      : group,
  );
}

export function getDataExportBreakdownView({
  dimension,
  scope,
  sources,
  pending,
}: {
  dimension: DimensionType;
  scope: DataExportScope;
  sources: DataExportSourceStates;
  pending?: PendingDestinationSource;
}): DataExportBreakdownView {
  const inScope = getInScopeData(dimension, scope, sources);
  if (inScope.status !== 'ready') {
    return { status: inScope.status };
  }
  const responses = inScope.data.map(({ data }) => data);
  const { groups, kinds, total } = sumGroups(dimension, responses);

  if (dimension === DimensionType.DESTINATION_ID && !pending) {
    const availability = getDestinationAvailability(responses);
    if (availability.status !== 'available') {
      return availability.status === 'availableFrom' ? { ...availability, total } : availability;
    }
  }

  const sorted = [...groups.values()]
    .filter((group) => group.total > 0)
    .sort((left, right) => right.total - left.total || left.label.localeCompare(right.label));
  const ranked = dimension === DimensionType.DESTINATION_ID ? disambiguateLabels(sorted, kinds) : sorted;
  return {
    status: 'ready',
    total,
    ranked,
    top: ranked.slice(0, BREAKDOWN_TOP_N),
  };
}

export function getBreakdownPreviousTotals({
  dimension,
  scope,
  sources,
}: {
  dimension: DimensionType;
  scope: DataExportScope;
  sources: DataExportSourceStates;
}): { total: number; byKey: Map<string, number> } | undefined {
  const inScope = getInScopeData(dimension, scope, sources);
  if (inScope.status !== 'ready') {
    return undefined;
  }
  const { groups, total } = sumGroups(
    dimension,
    inScope.data.map(({ data }) => data),
  );
  return { total, byKey: new Map([...groups].map(([key, group]) => [key, group.total])) };
}

export function getBreakdownPreviousRange({
  timeRange,
  from,
  to,
  today,
}: {
  timeRange?: TimeRangeOption;
  from: number;
  to: number;
  today: Date;
}): { from: number; to: number } {
  if (timeRange === undefined || timeRange === TimeRangeOption.MONTH_TO_DATE) {
    return getPreviousTimestampsUTC(from, today);
  }
  const length = to - from;
  const end = Math.min(to, getCutoffTimestampUTC(today, Granularity.HOURLY));
  return { from: from - length, to: end - length };
}

export type BreakdownChartSeries =
  | { status: 'loading' | 'error' | 'notYetAvailable' }
  | { status: 'ready'; byKey: Record<string, UsageSeriesList> };

export function getBreakdownChartSeries({
  dimension,
  scope,
  sources,
  keys,
  aggregationType,
}: {
  dimension: DimensionType;
  scope: DataExportScope;
  sources: DataExportSourceStates;
  keys: string[];
  aggregationType: AggregationType;
}): BreakdownChartSeries {
  const inScope = getInScopeData(dimension, scope, sources);
  if (inScope.status !== 'ready') {
    return { status: inScope.status };
  }
  const keySeries = (data: UsageSeriesList, key: string): UsageSeriesList => {
    const indexes = data.metadata.flatMap((metadata, index) =>
      getKey(dimension, metadata) === key ? [String(index)] : [],
    );
    return {
      _links: {},
      metadata: [],
      series: data.series.map((slice) => ({
        time: slice.time,
        '0': indexes.reduce((sum, index) => sum + (slice[index] ?? 0), 0),
      })),
    };
  };
  const bySource = (key: string, source: DataExportSource) => {
    const response = inScope.data.find((entry) => entry.source === source);
    return response && keySeries(response.data, key);
  };
  return {
    status: 'ready',
    byKey: Object.fromEntries(
      keys.map((key) => [
        key,
        alignDataExportSeries({
          streaming: bySource(key, DataExportScope.STREAMING),
          warehouse: bySource(key, DataExportScope.WAREHOUSE),
          aggregationType,
        }).combined,
      ]),
    ),
  };
}
